import { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  getAISalesDocuments, 
  uploadAISalesDocument, 
  deleteAISalesDocument,
  getAISalesVideos,
  createAISalesVideo,
  deleteAISalesVideo
} from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { 
  FileText, 
  Upload, 
  Trash2, 
  Target,
  Loader2,
  CheckCircle,
  Info,
  Video,
  Plus,
  X,
  Play
} from 'lucide-react';

export default function AdminAISalesKnowledgeBase() {
  const [documents, setDocuments] = useState([]);
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoForm, setVideoForm] = useState({ title: '', description: '', youtube_url: '', category: 'general' });
  const fileInputRef = useRef(null);

  const MAX_DOCUMENTS = 21;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [docsRes, videosRes] = await Promise.all([
        getAISalesDocuments(),
        getAISalesVideos()
      ]);
      setDocuments(docsRes.data);
      setVideos(videosRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = () => {
    if (documents.length >= MAX_DOCUMENTS) {
      toast.error(`Maximum ${MAX_DOCUMENTS} documents allowed.`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const filename = file.name.toLowerCase();
    if (!filename.endsWith('.pdf') && !filename.endsWith('.docx') && !filename.endsWith('.doc')) {
      toast.error('Only PDF and Word (.docx, .doc) files are allowed');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be less than 50MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      await uploadAISalesDocument(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      toast.success('Document uploaded successfully');
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload document';
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDoc = async (docId, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;

    try {
      await deleteAISalesDocument(docId);
      toast.success('Document deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleAddVideo = async () => {
    if (!videoForm.title || !videoForm.youtube_url) {
      toast.error('Title and YouTube URL are required');
      return;
    }

    try {
      await createAISalesVideo(videoForm);
      toast.success('Video added successfully');
      setShowVideoModal(false);
      setVideoForm({ title: '', description: '', youtube_url: '', category: 'general' });
      loadData();
    } catch (error) {
      toast.error('Failed to add video');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;

    try {
      await deleteAISalesVideo(videoId);
      toast.success('Video deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'general': 'General',
      'initial': 'Initial Contact',
      'discovery': 'Discovery',
      'proposal': 'Proposal',
      'negotiation': 'Negotiation',
      'closing': 'Closing'
    };
    return labels[category] || category;
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">AI Sales Knowledge Base</h1>
            <p className="text-slate-500 mt-1">
              Train the AI Sales Coach with sales methodology documents
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-800">How AI Sales Coach Works</h3>
                <p className="text-sm text-green-700 mt-1">
                  Upload sales playbooks, objection handling guides, closing techniques, and best practices.
                  The AI Sales Coach uses this knowledge to provide step-by-step guidance for moving prospects toward closure.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800">Training Documents</h2>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500">{documents.length} / {MAX_DOCUMENTS}</span>
              <Button 
                onClick={handleFileSelect}
                disabled={isUploading || documents.length >= MAX_DOCUMENTS}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                data-testid="upload-sales-doc-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload PDF/Word
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>

          {isUploading && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Loader2 className="w-6 h-6 animate-spin text-green-600" />
                  <div className="flex-1">
                    <p className="font-medium text-green-800">Processing document...</p>
                    <Progress value={uploadProgress} className="mt-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-600" />
            </div>
          ) : documents.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-700">No documents yet</h3>
                <p className="text-slate-500 text-sm mt-1">
                  Upload sales training materials to power the AI Sales Coach
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {documents.map((doc) => (
                <Card key={doc.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-800">{doc.original_filename}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-500">{formatDate(doc.upload_date)}</span>
                            <span className="flex items-center gap-1 text-xs text-green-600">
                              <CheckCircle className="w-3 h-3" /> Indexed
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteDoc(doc.id, doc.original_filename)}
                        className="hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Videos Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800">Training Videos</h2>
            <Button 
              onClick={() => setShowVideoModal(true)}
              variant="outline"
              className="border-green-200 text-green-700 hover:bg-green-50"
              data-testid="add-sales-video-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Video URL
            </Button>
          </div>

          {videos.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <Video className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No training videos added yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {videos.map((video) => (
                <Card key={video.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Play className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-800">{video.title}</h3>
                          <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded mt-1 inline-block">
                            {getCategoryLabel(video.category)}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteVideo(video.id)}
                        className="hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Add Video Modal */}
        {showVideoModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle>Add Training Video</CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setShowVideoModal(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Title *</label>
                  <Input
                    placeholder="e.g., Handling Price Objections"
                    value={videoForm.title}
                    onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">YouTube URL *</label>
                  <Input
                    placeholder="https://youtube.com/watch?v=..."
                    value={videoForm.youtube_url}
                    onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Category</label>
                  <select
                    value={videoForm.category}
                    onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })}
                    className="mt-1 w-full h-10 px-3 border border-slate-200 rounded-md text-sm"
                  >
                    <option value="general">General</option>
                    <option value="initial">Initial Contact</option>
                    <option value="discovery">Discovery</option>
                    <option value="proposal">Proposal</option>
                    <option value="negotiation">Negotiation</option>
                    <option value="closing">Closing</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Description</label>
                  <Input
                    placeholder="Brief description..."
                    value={videoForm.description}
                    onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowVideoModal(false)}>Cancel</Button>
                  <Button 
                    onClick={handleAddVideo}
                    className="bg-gradient-to-r from-green-600 to-emerald-600"
                  >
                    Add Video
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
